import { Box, Text, Textarea, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { QuestionOption } from './QuestionOption';
import type { ClarifyingQuestion, QuestionAnswer } from './types';

interface QuestionCardProps {
  question: ClarifyingQuestion;
  answer: QuestionAnswer | null;
  onAnswerChange: (answer: QuestionAnswer) => void;
  questionIndex: number;
  totalQuestions: number;
}

export function QuestionCard({
  question,
  answer,
  onAnswerChange,
  questionIndex,
  totalQuestions,
}: QuestionCardProps) {
  const [customText, setCustomText] = useState(answer?.freeText || '');

  // Sync customText when answer changes (e.g., when navigating between questions)
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    setCustomText(answer?.freeText || '');
  }, [answer?.freeText, question.id]);

  const handleOptionClick = (optionId: string) => {
    if (
      question.type === 'single_choice' ||
      question.type === 'choice_or_text'
    ) {
      if (optionId === 'other') {
        onAnswerChange({
          questionId: question.id,
          selectedOptionIds: [optionId],
          freeText: customText,
        });
      } else {
        setCustomText('');
        onAnswerChange({
          questionId: question.id,
          selectedOptionIds: [optionId],
        });
      }
    } else if (question.type === 'multi_choice') {
      const currentIds = answer?.selectedOptionIds || [];
      const newIds = currentIds.includes(optionId)
        ? currentIds.filter((id: string) => id !== optionId)
        : [...currentIds, optionId];
      onAnswerChange({
        questionId: question.id,
        selectedOptionIds: newIds,
      });
    }
  };

  const handleCustomTextChange = (text: string) => {
    setCustomText(text);
    onAnswerChange({
      questionId: question.id,
      selectedOptionIds: ['other'],
      freeText: text,
    });
  };

  const optionsWithDefaults = [
    ...question.options.slice(0, 2),
    { id: 'other', label: 'Other...', description: undefined },
  ];

  const isOptionSelected = (optionId: string) => {
    if (optionId === 'other') {
      return answer?.selectedOptionIds?.includes('other') || false;
    }
    return answer?.selectedOptionIds?.includes(optionId) || false;
  };

  return (
    <Box>
      <Text fontSize="2xs" color="gray.500" mb={1}>
        Question {questionIndex + 1} of {totalQuestions}
      </Text>
      <Text fontSize="sm" fontWeight="semibold" mb={2}>
        {question.title}
      </Text>
      {question.description && (
        <Text fontSize="xs" color="gray.600" mb={2}>
          {question.description}
        </Text>
      )}

      {(question.type === 'single_choice' ||
        question.type === 'multi_choice' ||
        question.type === 'choice_or_text') && (
        <VStack align="stretch" spacing={1.5} mb={0}>
          {optionsWithDefaults.map((option) => (
            <QuestionOption
              key={option.id}
              option={option}
              isSelected={isOptionSelected(option.id)}
              onClick={() => handleOptionClick(option.id)}
              type={
                question.type === 'multi_choice'
                  ? 'multi_choice'
                  : 'single_choice'
              }
              isEditable={option.id === 'other'}
              editValue={option.id === 'other' ? customText : undefined}
              onEditChange={
                option.id === 'other' ? handleCustomTextChange : undefined
              }
            />
          ))}
        </VStack>
      )}

      {question.type === 'free_text' && (
        <Textarea
          placeholder="Enter your response..."
          value={customText}
          onChange={(e) => handleCustomTextChange(e.target.value)}
          size="sm"
          minH="80px"
          mb={0}
        />
      )}
    </Box>
  );
}
